import { useMemo } from 'react';
import { AllowedExperimentKey } from '@tenants/types';
import { getEnvironment, isDevelopment, isVercel } from 'utils/environment';
import { ExperimentObservabilityService } from 'observability/services/experiment-observability';

/**
 * Cache for URL force overrides.
 * Parsed once per page load, shared across all hook instances.
 */
const urlOverrideCache = new Map<string, number>();
let urlOverrideCacheInitialized = false;


/**
 * Check if URL overrides are allowed in the current environment.
 * Only allow in development and preview (Vercel) environments.
 */
function isUrlOverrideAllowed(): boolean {
  return isDevelopment || isVercel;
}

/**
 * Initialize the URL override cache by parsing query parameters.
 * Only runs once per page load.
 */
function initUrlOverrideCache(): void {
  if (typeof window === 'undefined') {
    return;
  }
  
  if (urlOverrideCacheInitialized) {
    return;
  }

  // Only allow URL overrides in development and preview environments
  if (!isUrlOverrideAllowed()) {
    urlOverrideCacheInitialized = true;
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);
  
  urlParams.forEach((value, key) => {
    if (key.startsWith('gb_force_')) {
      const experimentKey = key.replace('gb_force_', '');
      const parsed = parseInt(value, 10);
      if (!isNaN(parsed)) {
        urlOverrideCache.set(experimentKey, parsed);
        ExperimentObservabilityService.trackUrlOverrideInit(
          experimentKey,
          parsed,
          getEnvironment()
        );
      }
    }
  });
  
  urlOverrideCacheInitialized = true;
}

export function resetUrlOverrideCacheForTesting() {
  urlOverrideCache.clear();
  urlOverrideCacheInitialized = false;
}

/**
 * Get URL force variation for a specific experiment.
 * Returns undefined if not in allowed environment or no override found.
 */
export function getUrlForceVariation(experimentKey: string): number | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  
  if (!isUrlOverrideAllowed()) {
    return undefined;
  }
  
  initUrlOverrideCache();
  return urlOverrideCache.get(experimentKey);
}

/**
 * Hook to get URL override for an experiment.
 * 
 * URL overrides allow forcing an experiment variation via query parameters.
 * Format: ?gb_force_experiment-key=1 (1 = variation, 0 = control)
 * 
 * Security: Only works in development and preview environments.
 * 
 * NOTE: This hook is now primarily for client-side observability tracking.
 * URL overrides are processed during SSR for hydration consistency.
 * 
 * @param experimentKey - The experiment key to check for overrides
 * @returns The override value (0, 1, or undefined if no override)
 */
export function useUrlOverride(experimentKey: AllowedExperimentKey): number | undefined {
  // Synchronously get URL override during render
  // useMemo ensures this only runs once per experimentKey
  return useMemo(() => getUrlForceVariation(experimentKey), [experimentKey]);
}
